import React, { useState, useEffect, useMemo } from "react";

import {
  Box,
  Button,
  Heading,
  Textarea,
  FormControl,
  FormLabel,
  Input,
  Select,
  Code,
  Flex,
  Divider,
  Alert,
  AlertIcon,
  Spinner,
  Text,
  useToast,
  Switch,
  IconButton,
  Tooltip,
  Badge,
  HStack,
  ButtonGroup,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Grid,
  GridItem,
  Checkbox, CheckboxGroup, Stack, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, FormHelperText
} from "@chakra-ui/react";
import { CopyIcon, CheckIcon, DownloadIcon, ViewIcon, DeleteIcon, EditIcon, SearchIcon } from "@chakra-ui/icons";
import { supabase } from "../integrations/supabase/client";
import { FaPlus } from "react-icons/fa";

const EdgeFunctionDebug = () => {
  const [functionName, setFunctionName] = useState("folo");
  const [payload, setPayload] = useState("{\n  \"test\": true\n}");
  const [result, setResult] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("Not Detected");
  const [supabaseUrl, setSupabaseUrl] = useState(import.meta.env.SUPABASE_URL || "https://your-project-url.supabase.co");
  const [availableFunctions, setAvailableFunctions] = useState(["folo", "hello-world", "test"]);
  const [hasCopied, setHasCopied] = useState(false);
  const [dbWriteResult, setDbWriteResult] = useState("");
  const [isWritingDb, setIsWritingDb] = useState(false);
  const [extractedData, setExtractedData] = useState("");
  const [isExtracting, setIsExtracting] = useState(false);
  const [actionResultType, setActionResultType] = useState(""); // "extracted" or "written"
  
  // Database access Tab state
  const [selectedTable, setSelectedTable] = useState("article");
  const [tableColumns, setTableColumns] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [tableError, setTableError] = useState(null);
  const [customQuery, setCustomQuery] = useState("SELECT * FROM article LIMIT 10;");
  const [queryResult, setQueryResult] = useState(null);
  const [isRunningQuery, setIsRunningQuery] = useState(false);
  
  // Filter functionality state
  const [filterColumn, setFilterColumn] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [filterOperator, setFilterOperator] = useState("eq");
  
  // Add limit state
  const [limit, setLimit] = useState(200);
  
  const toast = useToast();

  // JSON request templates
  const jsonTemplates = {
    "Blank Template": "{\n  \"test\": true\n}",
    "Get Article List": JSON.stringify({
      "path": "entries",
      "view": 0,
      "feedIdList": []
    }, null, 2),
    "Get Articles (Specified Quantity)": JSON.stringify({
      "path": "entries",
      "view": 0,
      "limit": 10,
      "feedIdList": []
    }, null, 2),
    "获取特定订阅源的文章": JSON.stringify({
      "path": "entries",
      "view": 0,
      "feedIdList": ["输入订阅源ID，例如: 52325519371718656"]
    }, null, 2),
    "获取多个订阅源文章": JSON.stringify({
      "path": "entries", 
      "view": 0,
      "feedIdList": [
        "订阅源ID-1", 
        "订阅源ID-2"
      ]
    }, null, 2),
    "按发布时间筛选文章": JSON.stringify({
      "path": "entries",
      "view": 0,
      "publishedAfter": new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      "feedIdList": []
    }, null, 2),
    "Get All Subscription List": JSON.stringify({
      "path": "subscriptions"
    }, null, 2),
    "Test Edge Function": JSON.stringify({
      "test": true,
      "timestamp": new Date().toISOString()
    }, null, 2)
  };

  // 处理模板选择变化
  const handleTemplateChange = (e) => {
    const selectedTemplate = e.target.value;
    if (jsonTemplates[selectedTemplate]) {
      setPayload(jsonTemplates[selectedTemplate]);
      toast({
        title: "Request template loaded",
        description: `Selected ${selectedTemplate} template`,
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    }
  };
  
  // 复制功能
  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(result);
      setHasCopied(true);
      toast({
        title: "已复制到剪贴板",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setTimeout(() => setHasCopied(false), 2000);
    } catch (err) {
      toast({
        title: "复制失败",
        description: "无法访问剪贴板",
        status: "error",
        duration: 2000,
      });
    }
  };

  // 修复extractKeyFields函数
  const extractKeyFields = () => {
    if (!result) {
      toast({
        title: "No data to extract",
        description: "Please fetch data first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsExtracting(true);
      setDbWriteResult(""); // Clear previous results
      setActionResultType("extracted");
      let resultData = {};
      let extractedItems = [];

      try {
        resultData = JSON.parse(result);
        console.log("Parsed data:", resultData);
      } catch (error) {
        throw new Error("Failed to parse result data");
      }

      // Extract article entry key fields
      if (resultData.data && Array.isArray(resultData.data.data)) {
        const articleItems = resultData.data.data;
        
        extractedItems.push(`# Extracted ${articleItems.length} articles`);
        extractedItems.push("");

        articleItems.forEach((item, index) => {
          extractedItems.push(`## ${index + 1}. ${item.entries?.title || 'No Title'}`);

          // Extract entries information
          if (item.entries) {
            extractedItems.push(`article_id: ${item.entries.id || 'None'}`);
            extractedItems.push(`title: ${item.entries.title || ''}`);
            extractedItems.push(`url: ${item.entries.url || ''}`);
            extractedItems.push(`description: ${item.entries.description || ''}`);
            extractedItems.push(`author: ${item.entries.author || ''}`);
            extractedItems.push(`published_at: ${item.entries.publishedAt || new Date().toISOString()}`);
            extractedItems.push(`guid: ${item.entries.guid || item.entries.id || ''}`);

            // Handle media information
            const media = Array.isArray(item.entries.media) && item.entries.media.length > 0 
              ? item.entries.media[0] 
              : null;
            
            extractedItems.push(`media_url: ${media?.url || ''}`);
            extractedItems.push(`media_type: ${media?.type || ''}`);
            
            extractedItems.push(`categories: ${JSON.stringify(item.entries.categories || [])}`);
          }
          
          // Extract feeds information
          if (item.feeds) {
            extractedItems.push(`feed_id: ${item.feeds.id || ''}`);
            extractedItems.push(`feed_title: ${item.feeds.title || ''}`);
            extractedItems.push(`feed_description: ${item.feeds.description || ''}`);
            extractedItems.push(`feed_site_url: ${item.feeds.siteUrl || ''}`);
            extractedItems.push(`feed_image: ${item.feeds.image || ''}`);
          }

          // Extract subscriptions information
          if (item.subscriptions) {
            extractedItems.push(`subscription_category: ${item.subscriptions.category || ''}`);
            extractedItems.push(`subscription_title: ${item.subscriptions.title || ''}`);
          }

          // Extract other top-level information
          extractedItems.push(`view: ${item.view || 0}`);
          extractedItems.push(`read: ${item.read === true}`);
          extractedItems.push(`from: ${JSON.stringify(item.from || [])}`);
          extractedItems.push("");
        });
      } else {
        // Generic extraction - extract summary of top-level fields
        extractedItems.push("# Data Summary");
        extractedItems.push("");

        for (const [key, value] of Object.entries(resultData)) {
          if (key === 'data' && typeof value === 'object') {
            extractedItems.push(`## Data Fields`);

            if (value.data && Array.isArray(value.data)) {
              extractedItems.push(`items_count: ${value.data.length}`);
              if (value.data.length > 0) {
                extractedItems.push(`data_type: ${typeof value.data[0]}`);
                extractedItems.push(`fields: ${Object.keys(value.data[0]).join(', ')}`);
              }
            } else {
              extractedItems.push(`type: ${typeof value}`);
              if (typeof value === 'object') {
                extractedItems.push(`fields: ${Object.keys(value).join(', ')}`);
              }
            }
          } else if (key !== 'headers') {
            extractedItems.push(`## ${key}`);
            extractedItems.push(`type: ${typeof value}`);
            extractedItems.push(`value: ${typeof value === 'object' ? JSON.stringify(value).substring(0, 100) + '...' : value}`);
          }
          extractedItems.push("");
        }
      }

      setDbWriteResult(extractedItems.join('\n'));
      
      toast({
        title: "提取完成",
        description: `成功提取关键字段`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("提取关键字段失败:", error);
      setDbWriteResult(`提取失败: ${error.message || "未知错误"}`);
      toast({
        title: "提取关键字段失败",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExtracting(false);
    }
  };

  // Write to database functionality
  const writeToDatabase = async () => {
    if (!result) {
      toast({
        title: "No data to write",
        description: "Please fetch data first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsWritingDb(true);
      setDbWriteResult(""); // Clear previous results
      setActionResultType("written");
      let resultData = {};

      try {
        resultData = JSON.parse(result);
      } catch (error) {
        throw new Error("Failed to parse result data");
      }

      // Determine request path and data type
      const isSubscriptions = functionName.includes("subscription") ||
                             (resultData.api_url && resultData.api_url.includes("subscription"));

      if (isSubscriptions) {
        await writeSubscriptionsToDb(resultData);
      } else if (functionName.includes("entries") || (resultData.api_url && resultData.api_url.includes("entries"))) {
        await writeEntriesToDb(resultData);
      } else {
        throw new Error("Unrecognized data type, please use subscriptions or entries data");
      }
    } catch (error) {
      console.error("Failed to write to database:", error);
      setDbWriteResult(`Write failed: ${error.message || "Unknown error"}`);
      toast({
        title: "Failed to write to database",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsWritingDb(false);
    }
  };

  // 写入订阅数据到feed表
  const writeSubscriptionsToDb = async (resultData) => {
    // 确保数据格式正确
    if (!resultData.data || !resultData.data.data || !Array.isArray(resultData.data.data)) {
      throw new Error("无效的subscriptions数据格式");
    }

    const feedItems = resultData.data.data;
    const writeResults = [];
    let successCount = 0;
    let errorCount = 0;

    for (const item of feedItems) {
      try {
        if (!item.feeds || !item.feeds.id) continue;

        const feed = {
          feed_id: item.feeds.id,
          view_id: item.view?.toString() || "1",
          category: item.category || "未分类",
          title: item.feeds.title || "",
          description: item.feeds.description || "",
          url: item.feeds.url || "",
          image: item.feeds.image || "",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        console.log(`尝试写入feed: ${JSON.stringify(feed)}`);

        // 使用upsert方法，指定feed_id作为冲突检测字段
        const { data, error } = await supabase
          .from('feed')
          .upsert([feed], { 
            onConflict: 'feed_id'
          });

        if (error) {
          console.error(`写入feed失败:`, error);
          errorCount++;
          writeResults.push(`❌ ${item.feeds.title || item.feeds.id}: ${error.message} (${error.code})`);
        } else {
          successCount++;
          writeResults.push(`✅ ${item.feeds.title || item.feeds.id}: 写入成功`);
        }
      } catch (err) {
        console.error(`处理feed项目异常:`, err);
        errorCount++;
        writeResults.push(`❌ 处理项目失败: ${err.message}`);
      }
    }

    const summary = `总结: 成功 ${successCount} 项, 失败 ${errorCount} 项`;
    setDbWriteResult([summary, ...writeResults].join('\n'));

    toast({
      title: `写入完成 (${successCount}/${successCount + errorCount})`,
      status: successCount > 0 ? (errorCount === 0 ? "success" : "warning") : "error",
      duration: 5000,
      isClosable: true,
    });
  };

  // 修复writeEntriesToDb函数
  const writeEntriesToDb = async (resultData) => {
    // 确保数据格式正确
    if (!resultData.data || !resultData.data.data || !Array.isArray(resultData.data.data)) {
      throw new Error("无效的entries数据格式");
    }

    const articleItems = resultData.data.data;
    const writeResults = [];
    let successCount = 0;
    let errorCount = 0;

    for (const item of articleItems) {
      try {
        // 检查数据结构并创建文章对象
        if (!item.entries || !item.entries.id) {
          console.warn("文章缺少entries或id:", item);
          continue;
        }

        const entry = item.entries;
        const media = Array.isArray(entry.media) && entry.media.length > 0 ? entry.media[0] : null;
        
        const article = {
          article_id: entry.id,
          title: entry.title || "无标题",
          url: entry.url || "",
          description: entry.description || "",
          author: entry.author || "",
          published_at: entry.publishedAt || new Date().toISOString(),
          guid: entry.guid || entry.id,
          feed_id: item.feeds?.id || "",
          categories: entry.categories || [],
          media_url: media?.url || "",
          media_type: media?.type || "",
          created_at: entry.insertedAt || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_used: false
        };

        console.log(`尝试写入article: ${JSON.stringify(article)}`);

        const { data, error } = await supabase
          .from('article')
          .upsert([article], { 
            onConflict: 'article_id'
          });

        if (error) {
          console.error(`写入article失败:`, error);
          errorCount++;
          writeResults.push(`❌ ${article.title || article.article_id}: ${error.message} (${error.code})`);
        } else {
          successCount++;
          writeResults.push(`✅ ${article.title || article.article_id}: 写入成功`);
        }
      } catch (err) {
        console.error(`处理article项目异常:`, err);
        errorCount++;
        writeResults.push(`❌ 处理项目失败: ${err.message}`);
      }
    }

    const summary = `总结: 成功 ${successCount} 项, 失败 ${errorCount} 项`;
    setDbWriteResult([summary, ...writeResults].join('\n'));

    toast({
      title: `写入完成 (${successCount}/${successCount + errorCount})`,
      status: successCount > 0 ? (errorCount === 0 ? "success" : "warning") : "error",
      duration: 5000,
      isClosable: true,
    });
  };

  // Check Supabase connection
  useEffect(() => {
    const checkConnection = async () => {
      try {
        setConnectionStatus("Detecting...");

        // Simple health check - use auth.getSession to test connection
        try {
          const { data, error } = await supabase.auth.getSession();

          if (!error) {
            console.log("Supabase connection successful");
            setConnectionStatus("Connected");
            return;
          } else {
            console.warn("Supabase session check failed:", error);
          }
        } catch (authError) {
          console.warn("Authentication check failed:", authError);
        }
        
        // Try another check method
        try {
          // Test if functions can be called
          const testResult = await supabase.functions.invoke("test", {
            body: { test: true }
          });

          if (!testResult.error) {
            console.log("Function call test successful");
            setConnectionStatus("Connected");
            return;
          } else {
            console.warn("Function call test failed:", testResult.error);
          }
        } catch (funcError) {
          console.warn("Function call test error:", funcError);
        }

        // All methods failed, but project might still work partially
        setConnectionStatus("Connected but Limited");
      } catch (e) {
        console.error("Supabase connection check error:", e);
        setConnectionStatus("Connection Error");
      }
    };
    
    checkConnection();
  }, []);

  // Call Edge Function
  const invokeFunction = async () => {
    setIsLoading(true);
    setError(null);
    setResult("");
    setDbWriteResult("");
    setActionResultType("");
    
    try {
      // Parse JSON payload
      const parsedPayload = payload ? JSON.parse(payload) : {};

      console.log(`Calling remote Edge Function: ${functionName}, parameters:`, parsedPayload);
      
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: parsedPayload
      });
      
      if (error) {
        throw error;
      }
      
      console.log("Edge Function returned result:", data);
      setResult(JSON.stringify(data, null, 2));

      toast({
        title: "Call Successful",
        description: "Remote Edge Function executed successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Edge Function call failed:", err);

      const errorMsg = err.message || "Error occurred while calling Edge Function";
      const detailMsg = err.details || err.stack || "";

      setError(`${errorMsg}\n${detailMsg}`);
      
      toast({
        title: "调用失败",
        description: errorMsg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 尝试格式化JSON
  const formatPayload = () => {
    try {
      const parsed = JSON.parse(payload);
      setPayload(JSON.stringify(parsed, null, 2));
    } catch (e) {
      toast({
        title: "JSON格式错误",
        description: e.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Force connection attempt
  const forceConnect = () => {
    setConnectionStatus("Connected");
    toast({
      title: "Connection Force Enabled",
      description: "You can now try calling Edge Function, but it may fail",
      status: "warning",
      duration: 5000,
      isClosable: true,
    });
  };

  // 获取操作结果标题
  const getActionTitle = () => {
    switch(actionResultType) {
      case "extracted": return "Extraction Results";
      case "written": return "Database Write Results";
      default: return "Operation Results";
    }
  };

  // 获取操作结果状态徽章
  const getActionBadge = () => {
    if (actionResultType === "extracted") {
      return <Badge colorScheme="blue" fontSize="sm">提取完成</Badge>;
    } else if (actionResultType === "written") {
      if (dbWriteResult.includes('失败')) {
        return <Badge colorScheme="red" fontSize="sm">部分失败</Badge>;
      } else {
        return <Badge colorScheme="green" fontSize="sm">全部成功</Badge>;
      }
    }
    return null;
  };

  // 以下是DB访问Tab的功能

  // 加载数据库表格数据
  const loadTableData = async () => {
    if (!selectedTable) {
      toast({
        title: "请选择一个表",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoadingTable(true);
      setTableError(null);
      
      // 获取表结构
      const { data: columnsData, error: columnsError } = await supabase
        .from(selectedTable)
        .select('*')
        .limit(1);
      
      if (columnsError) throw columnsError;
      
      if (columnsData && columnsData.length > 0) {
        setTableColumns(Object.keys(columnsData[0]));
      } else {
        // 如果没有数据，尝试获取表结构信息
        const { data: schemaData, error: schemaError } = await supabase
          .rpc('get_table_columns', { table_name: selectedTable });
        
        if (schemaError) throw schemaError;
        
        if (schemaData && Array.isArray(schemaData)) {
          setTableColumns(schemaData.map(col => col.column_name));
        } else {
          setTableColumns([]);
        }
      }
      
      // Build query
      let query = supabase
        .from(selectedTable)
        .select('*');
      
      // Add filter conditions
      if (filterColumn && filterValue && tableColumns.includes(filterColumn)) {
        // Apply different filter operators
        switch (filterOperator) {
          case "eq":
            query = query.eq(filterColumn, filterValue);
            break;
          case "neq":
            query = query.neq(filterColumn, filterValue);
            break;
          case "gt":
            query = query.gt(filterColumn, filterValue);
            break;
          case "gte":
            query = query.gte(filterColumn, filterValue);
            break;
          case "lt":
            query = query.lt(filterColumn, filterValue);
            break;
          case "lte":
            query = query.lte(filterColumn, filterValue);
            break;
          case "like":
            query = query.like(filterColumn, `%${filterValue}%`);
            break;
          case "ilike":
            query = query.ilike(filterColumn, `%${filterValue}%`);
            break;
          default:
            query = query.eq(filterColumn, filterValue);
        }
      }
      
      // Limit result count
      query = query.limit(100);
      
      // Execute query
      const { data, error } = await query;
      
      if (error) throw error;
      
      setTableData(data || []);
      
      let successMessage = `已加载${selectedTable}表的数据`;
      if (filterColumn && filterValue) {
        successMessage += `（已过滤：${filterColumn} ${getOperatorLabel(filterOperator)} ${filterValue}）`;
      }
      
      toast({
        title: "数据加载成功",
        description: successMessage,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("加载表数据失败:", error);
      setTableError(error.message);
      toast({
        title: "加载失败",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingTable(false);
    }
  };
  
  // Get operator display label
  const getOperatorLabel = (op) => {
    switch (op) {
      case "eq": return "=";
      case "neq": return "≠";
      case "gt": return ">";
      case "gte": return "≥";
      case "lt": return "<";
      case "lte": return "≤";
      case "like": return "包含";
      case "ilike": return "Contains (Case Insensitive)";
      default: return "=";
    }
  };

  // Run custom SQL query
  const runCustomQuery = async () => {
    if (!customQuery.trim()) {
      toast({
        title: "Please enter SQL query",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsRunningQuery(true);
      setQueryResult(null);
      
      // Use rpc function to execute SQL query (this function needs to be created in the database)
      const { data, error } = await supabase
        .rpc('execute_sql_query', { query_text: customQuery });
      
      if (error) throw error;
      
      setQueryResult({
        data: data,
        columns: data && data.length > 0 ? Object.keys(data[0]) : [],
        rowCount: data ? data.length : 0
      });
      
      toast({
        title: "Query executed successfully",
        description: `Returned ${data ? data.length : 0} results`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to execute SQL query:", error);
      setQueryResult({
        error: error.message
      });
      toast({
        title: "Query failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRunningQuery(false);
    }
  };
  
  // 获取表列表
  const [tables, setTables] = useState([]);
  
  useEffect(() => {
    const loadTables = async () => {
      try {
        // 假设我们有一个rpc函数来获取所有表
        const { data, error } = await supabase
          .rpc('get_tables');
        
        if (error) throw error;
        
        // If rpc function is not available, consider using the following query
        // This requires appropriate permissions and is not best practice
        if (!data) {
          const { data: fallbackData, error: fallbackError } = await supabase
            .from('pg_tables')
            .select('tablename')
            .eq('schemaname', 'public');
          
          if (fallbackError) throw fallbackError;
          
          if (fallbackData) {
            setTables(fallbackData.map(t => t.tablename));
          }
        } else {
          setTables(data);
        }
      } catch (error) {
        console.error("加载表列表失败:", error);
        // 设置一些默认表，以防获取失败
        setTables(['article', 'feed']);
      }
    };
    
    if (connectionStatus === "已连接" || connectionStatus === "已连接但受限") {
      loadTables();
    }
  }, [connectionStatus]);

  return (
    <Box maxW="900px" mx="auto" mt={8} p={5}>
      <Heading as="h1" mb={6}>Admin Dashboard</Heading>
      
      <Box mb={4}>
        <Alert 
          status={
            connectionStatus === "已连接" ? "success" : 
            connectionStatus === "已连接但受限" ? "success" :
            connectionStatus === "检测中..." ? "info" : 
            "warning"
          }
        >
          <AlertIcon />
          <Flex justify="space-between" width="100%" align="center">
            <Text>Supabase Status: {connectionStatus}</Text>

            {connectionStatus !== "Connected" && connectionStatus !== "Detecting..." && (
              <Button size="sm" colorScheme="yellow" onClick={forceConnect}>
                Force Enable
              </Button>
            )}
          </Flex>

          {(connectionStatus !== "Connected" && connectionStatus !== "Connected but Limited") && (
            <Text fontSize="sm" mt={2}>
              Please ensure your Supabase project is enabled and configured with correct environment variables
            </Text>
          )}
        </Alert>
      </Box>
      
      <Tabs variant="enclosed" colorScheme="teal">
        <TabList mb="1em">
          <Tab>Edge Function Runner</Tab>
          <Tab>Database Access</Tab>
          <Tab>Feed Update Fetch</Tab>
        </TabList>
        <TabPanels>
          {/* Edge Function Runner Tab */}
          <TabPanel>
            <Box mb={6} p={4} borderWidth="1px" borderRadius="lg">
              <Heading as="h2" size="md" mb={4}>Request Settings</Heading>

              <FormControl mb={4}>
                <FormLabel>Supabase URL</FormLabel>
                <Input
                  value={supabaseUrl}
                  isReadOnly
                  bg="gray.50"
                  placeholder="Configure SUPABASE_URL in environment variables"
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Select Function</FormLabel>
                <Select 
                  value={functionName}
                  onChange={(e) => setFunctionName(e.target.value)}
                >
                  {availableFunctions.map(fn => (
                    <option key={fn} value={fn}>{fn}</option>
                  ))}
                </Select>
                {availableFunctions.length <= 3 && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    显示的是默认函数列表。如果您已部署其他函数，可以直接在下拉框中选择。
                  </Text>
                )}
              </FormControl>
              
              <FormControl mb={4}>
                <FormLabel>Request Parameters (JSON)</FormLabel>
                <Select mb={2} onChange={handleTemplateChange} placeholder="Select request template...">
                  {Object.keys(jsonTemplates).map(template => (
                    <option key={template} value={template}>{template}</option>
                  ))}
                </Select>
                <Textarea
                  value={payload}
                  onChange={(e) => setPayload(e.target.value)}
                  placeholder="输入JSON参数"
                  size="md"
                  height="150px"
                  fontFamily="monospace"
                />
                <Flex mt={2} gap={4}>
                  <Button 
                    width="50%"
                    size="md"
                    colorScheme="teal" 
                    variant="solid"
                    onClick={formatPayload}
                  >
                    格式化JSON
                  </Button>
                  <Button 
                    width="50%"
                    size="md"
                    colorScheme="teal" 
                    variant="solid"
                    onClick={invokeFunction}
                    isLoading={isLoading}
                    loadingText="Calling..."
                    isDisabled={connectionStatus !== "Connected" && connectionStatus !== "Connected but Limited"}
                  >
                    Call Remote Function
                  </Button>
                </Flex>
              </FormControl>
              {connectionStatus !== "已连接" && connectionStatus !== "已连接但受限" && (
                <Text fontSize="sm" color="red.500" mt={2}>
                  Supabase not connected, cannot call Edge Function
                </Text>
              )}
            </Box>
            
            <Divider my={6} />
            
            <Box mb={6}>
              <Flex justify="space-between" align="center" mb={4}>
                <Heading as="h2" size="md">调用结果</Heading>
                
                {result && (
                  <HStack>
                    <Tooltip label="提取关键字段">
                      <IconButton
                        aria-label="提取关键字段"
                        icon={<ViewIcon />}
                        onClick={extractKeyFields}
                        size="sm"
                        colorScheme="purple"
                        isLoading={isExtracting}
                      />
                    </Tooltip>
                    <Tooltip label="Write to Database">
                      <IconButton
                        aria-label="Write to Database"
                        icon={<EditIcon />}
                        onClick={writeToDatabase}
                        size="sm"
                        colorScheme="green"
                        isDisabled={!result}
                        isLoading={isWritingDb}
                      />
                    </Tooltip>
                    <Tooltip label="复制结果">
                      <IconButton
                        aria-label="复制结果"
                        icon={hasCopied ? <CheckIcon /> : <CopyIcon />}
                        onClick={handleCopy}
                        size="sm"
                        colorScheme={hasCopied ? "green" : "blue"}
                      />
                    </Tooltip>
                  </HStack>
                )}
              </Flex>
              
              {isLoading && (
                <Flex justify="center" align="center" my={8}>
                  <Spinner size="lg" mr={3} />
                  <Text>Calling remote Edge Function...</Text>
                </Flex>
              )}
              
              {error && (
                <Alert status="error" mb={4} borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">错误:</Text>
                    <Code display="block" whiteSpace="pre" p={2}>
                      {error}
                    </Code>
                  </Box>
                </Alert>
              )}
              
              {!isLoading && !error && result && (
                <Box 
                  p={4} 
                  borderWidth="1px" 
                  borderRadius="lg" 
                  bg="gray.50" 
                  overflow="auto"
                  maxH="400px"
                  position="relative"
                >
                  <Code display="block" whiteSpace="pre" p={2} borderRadius="md">
                    {result}
                  </Code>
                </Box>
              )}
              
              {!isLoading && !error && !result && (
                <Text color="gray.500" textAlign="center">
                  Click "Call Remote Function" button to execute Edge Function
                </Text>
              )}
            </Box>
            
            {dbWriteResult && (
              <Box mb={6}>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading as="h2" size="md">{getActionTitle()}</Heading>
                  {getActionBadge()}
                </Flex>
                
                <Box 
                  p={4} 
                  borderWidth="1px" 
                  borderRadius="lg" 
                  bg="gray.50" 
                  overflow="auto"
                  maxH="400px"
                >
                  {actionResultType === "extracted" ? (
                    <Box whiteSpace="pre-wrap" p={2}>
                      {dbWriteResult}
                    </Box>
                  ) : (
                    <Code display="block" whiteSpace="pre" p={2} borderRadius="md">
                      {dbWriteResult}
                    </Code>
                  )}
                </Box>
              </Box>
            )}
          </TabPanel>
          
          {/* 数据库访问Tab */}
          <TabPanel>
            <Box mb={6} p={4} borderWidth="1px" borderRadius="lg">
              <Heading as="h2" size="md" mb={4}>Database Table Query</Heading>
              
              <Grid templateColumns="repeat(12, 1fr)" gap={4} mb={4}>
                {/* Table Selection */}
                <GridItem colSpan={{ base: 12, md: 4 }}>
                  <FormControl>
                    <FormLabel>Select Data Table</FormLabel>
                    <Select
                      value={selectedTable}
                      onChange={(e) => {
                        setSelectedTable(e.target.value);
                        setFilterColumn(""); // Reset filter column
                        setFilterValue(""); // Reset filter value
                      }}
                    >
                      {tables.map(table => (
                        <option key={table} value={table}>{table}</option>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
                
                {/* Filter Column Selection */}
                <GridItem colSpan={{ base: 12, md: 3 }}>
                  <FormControl>
                    <FormLabel>Filter Field</FormLabel>
                    <Select
                      value={filterColumn}
                      onChange={(e) => setFilterColumn(e.target.value)}
                      placeholder="选择过滤字段..."
                    >
                      {tableColumns.map(column => (
                        <option key={column} value={column}>{column}</option>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
                
                {/* Filter Operator */}
                <GridItem colSpan={{ base: 6, md: 2 }}>
                  <FormControl>
                    <FormLabel>Operator</FormLabel>
                    <Select
                      value={filterOperator}
                      onChange={(e) => setFilterOperator(e.target.value)}
                    >
                      <option value="eq">=</option>
                      <option value="neq">≠</option>
                      <option value="gt">&gt;</option>
                      <option value="gte">≥</option>
                      <option value="lt">&lt;</option>
                      <option value="lte">≤</option>
                      <option value="like">包含</option>
                      <option value="ilike">Contains (Case Insensitive)</option>
                    </Select>
                  </FormControl>
                </GridItem>
                
                {/* Filter Value Input */}
                <GridItem colSpan={{ base: 6, md: 3 }}>
                  <FormControl>
                    <FormLabel>Filter Value</FormLabel>
                    <Input
                      value={filterValue}
                      onChange={(e) => setFilterValue(e.target.value)}
                      placeholder="Enter filter value..."
                    />
                  </FormControl>
                </GridItem>
              </Grid>
              
              {/* Query Button */}
              <Flex justifyContent="flex-end" mb={4}>
                <Button
                  leftIcon={<SearchIcon />}
                  colorScheme="teal"
                  onClick={loadTableData}
                  isLoading={isLoadingTable}
                  loadingText="Loading..."
                >
                  Query Data
                </Button>
              </Flex>
              
              {tableError && (
                <Alert status="error" mb={4} borderRadius="md">
                  <AlertIcon />
                  <Text>{tableError}</Text>
                </Alert>
              )}
              
              {isLoadingTable && (
                <Flex justify="center" my={8}>
                  <Spinner size="lg" mr={3} />
                  <Text>加载数据中...</Text>
                </Flex>
              )}
              
              {!isLoadingTable && tableData.length > 0 && (
                <>
                  <Text mb={2}>共 {tableData.length} 条记录</Text>
                  <Box overflow="auto" maxH="400px" borderWidth="1px" borderRadius="lg">
                    <Table variant="simple" size="sm">
                      <Thead position="sticky" top={0} bg="gray.100">
                        <Tr>
                          {tableColumns.map(column => (
                            <Th key={column}>{column}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {tableData.map((row, rowIndex) => (
                          <Tr key={rowIndex}>
                            {tableColumns.map(column => (
                              <Td key={`${rowIndex}-${column}`}>
                                {typeof row[column] === 'object' 
                                  ? JSON.stringify(row[column]) 
                                  : String(row[column] ?? '')}
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </>
              )}
              
              {!isLoadingTable && tableData.length === 0 && !tableError && (
                <Text textAlign="center" color="gray.500" my={8}>
                  {filterValue ? "No data found matching the criteria" : 'Select a table and click "Query Data" button'}
                </Text>
              )}
            </Box>
            
            <Divider my={6} />
            
            <Box mb={6} p={4} borderWidth="1px" borderRadius="lg">
              <Heading as="h2" size="md" mb={4}>SQL Query Tool</Heading>

              <FormControl mb={4}>
                <FormLabel>SQL Query</FormLabel>
                <Textarea
                  value={customQuery}
                  onChange={(e) => setCustomQuery(e.target.value)}
                  placeholder="Enter SQL query statement"
                  size="md"
                  height="120px"
                  fontFamily="monospace"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Note: Only query operations are supported, modification operations may be rejected.
                </Text>
              </FormControl>
              
              <Button
                colorScheme="teal"
                onClick={runCustomQuery}
                isLoading={isRunningQuery}
                loadingText="Executing..."
                mb={4}
              >
                Execute Query
              </Button>
              
              {isRunningQuery && (
                <Flex justify="center" my={4}>
                  <Spinner size="md" mr={3} />
                  <Text>Executing query...</Text>
                </Flex>
              )}
              
              {queryResult && queryResult.error && (
                <Alert status="error" mb={4} borderRadius="md">
                  <AlertIcon />
                  <Text>{queryResult.error}</Text>
                </Alert>
              )}
              
              {queryResult && queryResult.data && (
                <Box>
                  <Text mb={2}>返回 {queryResult.rowCount} 条结果</Text>
                  <Box overflow="auto" maxH="300px" borderWidth="1px" borderRadius="lg">
                    <Table variant="simple" size="sm">
                      <Thead position="sticky" top={0} bg="gray.100">
                        <Tr>
                          {queryResult.columns.map(column => (
                            <Th key={column}>{column}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {queryResult.data.map((row, rowIndex) => (
                          <Tr key={rowIndex}>
                            {queryResult.columns.map(column => (
                              <Td key={`${rowIndex}-${column}`}>
                                {typeof row[column] === 'object' 
                                  ? JSON.stringify(row[column]) 
                                  : String(row[column] ?? '')}
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </Box>
          </TabPanel>
          
          {/* Feed更新获取Tab */}
          <TabPanel>
            <FeedEntriesPanel />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default EdgeFunctionDebug;

const FeedEntriesPanel = () => {
  const [feedList, setFeedList] = useState([]);
  const [selectedFeeds, setSelectedFeeds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFeeds, setIsLoadingFeeds] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState(null);
  const [dbWriteResult, setDbWriteResult] = useState("");
  const [isExtracting, setIsExtracting] = useState(false);
  const [isWritingDb, setIsWritingDb] = useState(false);
  const [actionResultType, setActionResultType] = useState(""); // "extracted" 或 "written"
  const [limit, setLimit] = useState(200); // 添加limit状态
  
  const toast = useToast();

  // 获取所有feed
  useEffect(() => {
    const fetchFeeds = async () => {
      setIsLoadingFeeds(true);
      try {
        const { data, error } = await supabase
          .from('feed')
          .select('feed_id, title, category')
          .order('title');
          
        if (error) throw error;
        
        setFeedList(data || []);
      } catch (err) {
        console.error('获取feed列表失败:', err);
        toast({
          title: "获取feed失败",
          description: err.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoadingFeeds(false);
      }
    };
    
    fetchFeeds();
  }, [toast]);
  
  // 恢复为原来的实现方式，一次性发送所有选中的feedId
  const fetchSelectedFeeds = async () => {
    if (selectedFeeds.length === 0) {
      toast({
        title: "Please select Feed first",
        description: "Select at least one Feed to get its articles",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult("");
    
    try {
      // 构建请求参数，一次性发送所有选中的feedId
      const payload = {
        path: "entries",
        view: 0,
        feedIdList: selectedFeeds,
        limit: parseInt(limit) || 200 // 添加limit参数
      };
      
      console.log('调用参数:', payload);
      
      // 调用Edge Function
      const { data, error } = await supabase.functions.invoke("folo", {
        body: payload
      });
      
      if (error) throw error;
      
      setResult(JSON.stringify(data, null, 2));
      
      toast({
        title: "Fetch Successful",
        description: `Retrieved article data from ${selectedFeeds.length} Feeds`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error("获取Feed文章失败:", err);
      setError(err.message || "调用API时发生错误");
      
      toast({
        title: "获取失败",
        description: err.message || "调用API时发生错误",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // 按分类分组feed
  const feedsByCategory = useMemo(() => {
    const grouped = {};
    
    feedList.forEach(feed => {
      const category = feed.category || '未分类';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(feed);
    });
    
    return grouped;
  }, [feedList]);
  
  const handleFeedSelect = (values) => {
    setSelectedFeeds(values);
  };
  
  // 提取关键字段功能
  const extractKeyFields = () => {
    if (!result) {
      toast({
        title: "No data to extract",
        description: "Please fetch data first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsExtracting(true);
      setDbWriteResult(""); // Clear previous results
      setActionResultType("extracted");
      let resultData = {};
      let extractedItems = [];

      try {
        resultData = JSON.parse(result);
      } catch (error) {
        throw new Error("Failed to parse result data");
      }

      // Extract article entry key fields - only handle new data structure
      if (resultData.data && Array.isArray(resultData.data.data)) {
        const articleItems = resultData.data.data;

        extractedItems.push(`# Extracted ${articleItems.length} articles`);
        extractedItems.push("");

        articleItems.forEach((item, index) => {
          extractedItems.push(`## ${index + 1}. ${item.entries?.title || 'No Title'}`);

          // Extract entries information
          if (item.entries) {
            extractedItems.push(`article_id: ${item.entries.id || 'None'}`);
            extractedItems.push(`title: ${item.entries.title || ''}`);
            extractedItems.push(`url: ${item.entries.url || ''}`);
            extractedItems.push(`description: ${item.entries.description || ''}`);
            extractedItems.push(`author: ${item.entries.author || ''}`);
            extractedItems.push(`published_at: ${item.entries.publishedAt || new Date().toISOString()}`);
            extractedItems.push(`guid: ${item.entries.guid || item.entries.id || ''}`);

            // Handle media information
            const media = Array.isArray(item.entries.media) && item.entries.media.length > 0 
              ? item.entries.media[0] 
              : null;
            
            extractedItems.push(`media_url: ${media?.url || ''}`);
            extractedItems.push(`media_type: ${media?.type || ''}`);
            
            extractedItems.push(`categories: ${JSON.stringify(item.entries.categories || [])}`);
          }
          
          // 提取feeds信息
          if (item.feeds) {
            extractedItems.push(`feed_id: ${item.feeds.id || ''}`);
            extractedItems.push(`feed_title: ${item.feeds.title || ''}`);
            extractedItems.push(`feed_description: ${item.feeds.description || ''}`);
            extractedItems.push(`feed_site_url: ${item.feeds.siteUrl || ''}`);
            extractedItems.push(`feed_image: ${item.feeds.image || ''}`);
          }
          
          // 提取subscriptions信息
          if (item.subscriptions) {
            extractedItems.push(`subscription_category: ${item.subscriptions.category || ''}`);
            extractedItems.push(`subscription_title: ${item.subscriptions.title || ''}`);
          }
          
          // 提取其他顶级信息
          extractedItems.push(`view: ${item.view || 0}`);
          extractedItems.push(`read: ${item.read === true}`);
          extractedItems.push(`from: ${JSON.stringify(item.from || [])}`);
          extractedItems.push("");
        });
      } else {
        // 通用提取 - 提取顶级字段的摘要
        extractedItems.push("# 数据摘要");
        extractedItems.push("");
        
        for (const [key, value] of Object.entries(resultData)) {
          if (key === 'data' && typeof value === 'object') {
            extractedItems.push(`## 数据字段`);
            
            if (value.data && Array.isArray(value.data)) {
              extractedItems.push(`items_count: ${value.data.length}`);
              if (value.data.length > 0) {
                extractedItems.push(`data_type: ${typeof value.data[0]}`);
                extractedItems.push(`fields: ${Object.keys(value.data[0]).join(', ')}`);
              }
            } else {
              extractedItems.push(`type: ${typeof value}`);
              if (typeof value === 'object') {
                extractedItems.push(`fields: ${Object.keys(value).join(', ')}`);
              }
            }
          } else if (key !== 'headers') {
            extractedItems.push(`## ${key}`);
            extractedItems.push(`type: ${typeof value}`);
            extractedItems.push(`value: ${typeof value === 'object' ? JSON.stringify(value).substring(0, 100) + '...' : value}`);
          }
          extractedItems.push("");
        }
      }

      setDbWriteResult(extractedItems.join('\n'));
      
      toast({
        title: "提取完成",
        description: `成功提取关键字段`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("提取关键字段失败:", error);
      setDbWriteResult(`提取失败: ${error.message || "未知错误"}`);
      toast({
        title: "提取关键字段失败",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExtracting(false);
    }
  };

  // Write to database functionality
  const writeToDatabase = async () => {
    if (!result) {
      toast({
        title: "No data to write",
        description: "Please fetch data first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsWritingDb(true);
      setDbWriteResult(""); // Clear previous results
      setActionResultType("written");
      let resultData = {};

      try {
        resultData = JSON.parse(result);
      } catch (error) {
        throw new Error("Failed to parse result data");
      }

      // Write entry data to article table
      await writeEntriesToDb(resultData);
    } catch (error) {
      console.error("Failed to write to database:", error);
      setDbWriteResult(`Write failed: ${error.message || "Unknown error"}`);
      toast({
        title: "Failed to write to database",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsWritingDb(false);
    }
  };

  // 写入条目数据到article表
  const writeEntriesToDb = async (resultData) => {
    // 确保数据格式正确
    if (!resultData.data || !resultData.data.data || !Array.isArray(resultData.data.data)) {
      throw new Error("无效的entries数据格式");
    }

    const articleItems = resultData.data.data;
    const writeResults = [];
    let successCount = 0;
    let errorCount = 0;

    for (const item of articleItems) {
      try {
        // 检查数据结构并创建文章对象
        if (!item.entries || !item.entries.id) {
          console.warn("文章缺少entries或id:", item);
          continue;
        }

        const entry = item.entries;
        const media = Array.isArray(entry.media) && entry.media.length > 0 ? entry.media[0] : null;
        
        const article = {
          article_id: entry.id,
          title: entry.title || "无标题",
          url: entry.url || "",
          description: entry.description || "",
          author: entry.author || "",
          published_at: entry.publishedAt || new Date().toISOString(),
          guid: entry.guid || entry.id,
          feed_id: item.feeds?.id || "",
          categories: entry.categories || [],
          media_url: media?.url || "",
          media_type: media?.type || "",
          created_at: entry.insertedAt || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_used: false
        };

        console.log(`尝试写入article: ${JSON.stringify(article)}`);

        const { data, error } = await supabase
          .from('article')
          .upsert([article], { 
            onConflict: 'article_id'
          });

        if (error) {
          console.error(`写入article失败:`, error);
          errorCount++;
          writeResults.push(`❌ ${article.title || article.article_id}: ${error.message} (${error.code})`);
        } else {
          successCount++;
          writeResults.push(`✅ ${article.title || article.article_id}: 写入成功`);
        }
      } catch (err) {
        console.error(`处理article项目异常:`, err);
        errorCount++;
        writeResults.push(`❌ 处理项目失败: ${err.message}`);
      }
    }

    const summary = `总结: 成功 ${successCount} 项, 失败 ${errorCount} 项`;
    setDbWriteResult([summary, ...writeResults].join('\n'));

    toast({
      title: `写入完成 (${successCount}/${successCount + errorCount})`,
      status: successCount > 0 ? (errorCount === 0 ? "success" : "warning") : "error",
      duration: 5000,
      isClosable: true,
    });
  };

  // 获取操作结果标题
  const getActionTitle = () => {
    switch(actionResultType) {
      case "extracted": return "Extraction Results";
      case "written": return "Database Write Results";
      default: return "Operation Results";
    }
  };

  // 获取操作结果状态徽章
  const getActionBadge = () => {
    if (actionResultType === "extracted") {
      return <Badge colorScheme="blue" fontSize="sm">提取完成</Badge>;
    } else if (actionResultType === "written") {
      if (dbWriteResult.includes('失败')) {
        return <Badge colorScheme="red" fontSize="sm">部分失败</Badge>;
      } else {
        return <Badge colorScheme="green" fontSize="sm">全部成功</Badge>;
      }
    }
    return null;
  };
  
  return (
    <Box mb={6} p={4} borderWidth="1px" borderRadius="lg">
      <Heading as="h2" size="md" mb={4}>Feed Article Fetch</Heading>
      
      <Box mb={4}>
        <FormControl>
          <FormLabel>Article Quantity Limit</FormLabel>
          <NumberInput 
            defaultValue={200} 
            min={1} 
            max={1000}
            onChange={(_, value) => setLimit(value)}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <FormHelperText>Maximum number of articles to fetch per Feed</FormHelperText>
        </FormControl>
      </Box>
      
      <Box mb={6}>
        <FormControl>
          <FormLabel>选择要获取的Feed</FormLabel>
          {isLoadingFeeds ? (
            <Flex justify="center" my={4}>
              <Spinner size="md" mr={3} />
              <Text>加载Feed列表中...</Text>
            </Flex>
          ) : (
            <Box maxH="300px" overflowY="auto" p={2} borderWidth="1px" borderRadius="md">
              <Flex justify="space-between" align="center" mb={3} px={1}>
                <Button 
                  size="sm" 
                  colorScheme="blue" 
                  variant="outline"
                  leftIcon={<CheckIcon />}
                  onClick={() => {
                    const allFeedIds = feedList.map(feed => feed.feed_id);
                    // If current selection equals all, clear selection; otherwise select all
                    if (selectedFeeds.length === allFeedIds.length) {
                      setSelectedFeeds([]);
                    } else {
                      setSelectedFeeds(allFeedIds);
                    }
                  }}
                >
                  {selectedFeeds.length === feedList.length ? "Deselect All" : "Select All"}
                </Button>
                <Text fontSize="sm" color="blue.600" fontWeight="medium">
                  Total {feedList.length} Feeds
                </Text>
              </Flex>
              <CheckboxGroup colorScheme="blue" value={selectedFeeds} onChange={handleFeedSelect}>
                <Stack spacing={3}>
                  {/* 按分类显示Feed */}
                  {Object.entries(feedsByCategory).map(([category, feeds]) => (
                    <Box key={category} mb={3}>
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text fontWeight="bold" color="blue.600">{category}</Text>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="blue"
                          onClick={() => {
                            // 获取当前类别的所有feed_id
                            const categoryFeedIds = feeds.map(feed => feed.feed_id);
                            
                            // 检查当前类别的feed是否全部选中
                            const isAllSelected = categoryFeedIds.every(id => selectedFeeds.includes(id));
                            
                            if (isAllSelected) {
                              // 如果全部选中，则取消选中该类别的所有feed
                              setSelectedFeeds(selectedFeeds.filter(id => !categoryFeedIds.includes(id)));
                            } else {
                              // 如果未全部选中，则选中该类别的所有feed
                              const newSelectedFeeds = [...selectedFeeds];
                              categoryFeedIds.forEach(id => {
                                if (!newSelectedFeeds.includes(id)) {
                                  newSelectedFeeds.push(id);
                                }
                              });
                              setSelectedFeeds(newSelectedFeeds);
                            }
                          }}
                        >
                          {feeds.every(feed => selectedFeeds.includes(feed.feed_id))
                            ? "Deselect All"
                            : "Select All in Category"}
                        </Button>
                      </Flex>
                      <Stack pl={4} spacing={1}>
                        {feeds.map(feed => (
                          <Checkbox key={feed.feed_id} value={feed.feed_id}>
                            {feed.title} ({feed.feed_id})
                          </Checkbox>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CheckboxGroup>
            </Box>
          )}
          
          <Text fontSize="sm" color="gray.500" mt={1}>
            Selected {selectedFeeds.length} Feeds
          </Text>
        </FormControl>
        
        <Button
          colorScheme="teal"
          onClick={fetchSelectedFeeds}
          isLoading={isLoading}
          loadingText="Fetching..."
          isDisabled={selectedFeeds.length === 0}
        >
          Fetch Articles from Selected Feeds
        </Button>
      </Box>
      
      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">错误:</Text>
            <Code display="block" whiteSpace="pre" p={2}>
              {error}
            </Code>
          </Box>
        </Alert>
      )}
      
      {result && (
        <Box>
          <Flex justify="space-between" align="center" mb={2}>
            <Heading as="h3" size="sm">API返回结果</Heading>
            <HStack>
              <Tooltip label="提取关键字段">
                <IconButton
                  aria-label="提取关键字段"
                  icon={<ViewIcon />}
                  onClick={extractKeyFields}
                  size="sm"
                  colorScheme="purple"
                  isLoading={isExtracting}
                />
              </Tooltip>
              <Tooltip label="Write to Database">
                <IconButton
                  aria-label="Write to Database"
                  icon={<EditIcon />}
                  onClick={writeToDatabase}
                  size="sm"
                  colorScheme="green"
                  isDisabled={!result}
                  isLoading={isWritingDb}
                />
              </Tooltip>
              <Tooltip label="复制结果">
                <IconButton
                  aria-label="复制结果"
                  icon={<CopyIcon />}
                  onClick={() => {
                    navigator.clipboard.writeText(result);
                    toast({
                      title: "已复制",
                      status: "success",
                      duration: 2000,
                    });
                  }}
                  size="sm"
                  colorScheme="blue"
                />
              </Tooltip>
            </HStack>
          </Flex>
          <Box 
            p={4} 
            borderWidth="1px" 
            borderRadius="lg" 
            bg="gray.50" 
            overflow="auto"
            maxH="400px"
          >
            <Code display="block" whiteSpace="pre" p={2} borderRadius="md">
              {result}
            </Code>
          </Box>
        </Box>
      )}
      
      {dbWriteResult && (
        <Box mt={6}>
          <Flex justify="space-between" align="center" mb={4}>
            <Heading as="h3" size="sm">{getActionTitle()}</Heading>
            {getActionBadge()}
          </Flex>
          
          <Box 
            p={4} 
            borderWidth="1px" 
            borderRadius="lg" 
            bg="gray.50" 
            overflow="auto"
            maxH="400px"
          >
            {actionResultType === "extracted" ? (
              <Box whiteSpace="pre-wrap" p={2}>
                {dbWriteResult}
              </Box>
            ) : (
              <Code display="block" whiteSpace="pre" p={2} borderRadius="md">
                {dbWriteResult}
              </Code>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

